<template>
  <!--
    ── Lo que falta, en un widget ───────────────────────────────────────────
    El asistente pregunta con campos (ask_user) en vez de con un párrafo de
    preguntas: elegir entre varios que se parecen, buscar un registro de
    verdad, un día, una frase. Todo se contesta de una vez, y la respuesta
    vuelve a la conversación con sus ids — nada se teclea dos veces.
  -->
  <form class="ask" @submit.prevent="submit">
    <p v-if="widget.prompt" class="ask__prompt">{{ widget.prompt }}</p>

    <div v-for="field in widget.fields" :key="field.id" class="ask__field">
      <span class="ask__label">{{ field.label }}<span v-if="field.required" aria-hidden="true"> *</span></span>

      <!-- Elegir: cuando hay dudas entre registros que se parecen -->
      <div v-if="field.kind === 'choice'" class="options" role="group" :aria-label="field.label">
        <button
          v-for="option in field.options" :key="option.value"
          type="button"
          class="option"
          :class="{ 'option--on': isChosen(field, option.value) }"
          :aria-pressed="isChosen(field, option.value)"
          @click="choose(field, option.value)"
        >
          <span class="option__label">{{ option.label }}</span>
          <span v-if="option.hint" class="option__hint">{{ option.hint }}</span>
        </button>
      </div>

      <!-- Buscar entre registros reales -->
      <template v-else-if="field.kind === 'pick'">
        <div v-if="picksOf(field).length" class="chips">
          <span v-for="(pick, index) in picksOf(field)" :key="pick.id" class="chip">
            {{ pick.name }}
            <button type="button" class="chip__x" :aria-label="t('common.remove')" @click="removePick(field, index)">×</button>
          </span>
        </div>
        <UiReferenceField
          v-if="field.multiple || !picksOf(field).length"
          v-model="search[field.id].name"
          v-model:reference-id="search[field.id].id"
          :type="field.refType"
          :placeholder="t('assistant.card.search')"
          @picked="entry => pick(field, entry)"
        />
      </template>

      <!-- Un día -->
      <template v-else-if="field.kind === 'date'">
        <div class="chips">
          <button v-for="shortcut in dateShortcuts" :key="shortcut.value" type="button" class="chip chip--button" :class="{ 'chip--on': values[field.id] === shortcut.value }" @click="values[field.id] = shortcut.value">{{ shortcut.label }}</button>
        </div>
        <input v-model="values[field.id]" type="date" class="native-date" :aria-label="field.label">
      </template>

      <!-- Una frase -->
      <UiInput v-else v-model="values[field.id]" :placeholder="field.placeholder" />
    </div>

    <div class="ask__buttons">
      <UiButton type="submit" :disabled="!ready || store.sending" :loading="store.sending">{{ t('assistant.ask.continue') }}</UiButton>
    </div>
    <p class="ask__hint">{{ t('assistant.ask.orType') }}</p>
  </form>
</template>

<script setup>
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiInput, UiReferenceField } from '@general/ui-kit'

import { useAssistantStore } from '../store'

const props = defineProps({ widget: { type: Object, required: true } })

const { t } = useI18n()
const store = useAssistantStore()

const values = reactive({})
const search = reactive(Object.fromEntries(props.widget.fields.map(field => [field.id, { name: field.search ?? '', id: null }])))

const iso = (date) => date.toISOString().slice(0, 10)
const dateShortcuts = computed(() => {
  const day = (offset) => { const date = new Date(); date.setDate(date.getDate() + offset); return iso(date) }
  return [
    { value: day(0), label: t('assistant.card.today') },
    { value: day(1), label: t('assistant.card.tomorrow') },
    { value: day(2), label: t('assistant.card.afterTomorrow') },
  ]
})

const isChosen = (field, value) => (field.multiple ? (values[field.id] ?? []).includes(value) : values[field.id] === value)

function choose(field, value) {
  if (!field.multiple) {
    values[field.id] = value
    // Una sola pregunta de elegir uno: tocarlo ES contestar. Un botón más que
    // pulsar después es justo el esfuerzo que esto existe para quitar.
    if (props.widget.fields.length === 1) submit()
    return
  }
  const list = values[field.id] ?? []
  values[field.id] = list.includes(value) ? list.filter(item => item !== value) : [...list, value]
}

const picksOf = (field) => values[field.id] ?? []

function pick(field, entry) {
  if (!entry) return
  const picked = { id: entry.id, name: entry.label }
  values[field.id] = field.multiple
    ? [...picksOf(field).filter(item => item.id !== picked.id), picked]
    : [picked]
  search[field.id] = { name: '', id: null }
}

function removePick(field, index) {
  values[field.id] = picksOf(field).filter((_, at) => at !== index)
}

const answered = (field) => {
  const value = values[field.id]
  return Array.isArray(value) ? value.length > 0 : Boolean(value)
}
const ready = computed(() => props.widget.fields.every(field => !field.required || answered(field)))

/** La respuesta en palabras, para el hilo: lo que se eligió, como se lee. */
function inWords() {
  return props.widget.fields.filter(answered).map((field) => {
    const value = values[field.id]
    if (field.kind === 'choice') {
      const labels = (Array.isArray(value) ? value : [value]).map(item => field.options.find(option => option.value === item)?.label ?? item)
      return `${field.label}: ${labels.join(', ')}`
    }
    if (field.kind === 'pick') return `${field.label}: ${value.map(item => item.name).join(', ')}`
    if (field.kind === 'date') {
      return `${field.label}: ${new Date(`${value}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}`
    }
    return `${field.label}: ${value}`
  }).join(' · ')
}

function submit() {
  if (!ready.value || store.sending) return
  const answer = Object.fromEntries(props.widget.fields.filter(answered).map((field) => {
    const value = values[field.id]
    // Un buscador de uno viaja como el registro, no como una lista de uno.
    return [field.id, field.kind === 'pick' && !field.multiple ? value[0] : value]
  }))
  store.answerWidget(answer, inWords())
}
</script>

<style scoped>
.ask {
  border: 1px solid var(--color-primary);
  border-radius: var(--radius);
  background: var(--color-surface);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.ask__prompt { margin: 0; font-weight: 600; }
.ask__field { display: flex; flex-direction: column; gap: 6px; }
.ask__label { font-size: var(--text-sm); color: var(--color-text-soft); }

.options { display: flex; flex-direction: column; gap: 6px; }
.option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  cursor: pointer;
  min-height: 52px;
}
.option:hover { border-color: var(--color-primary); }
.option--on { border-color: var(--color-primary); background: var(--color-primary-soft); }
.option__label { font-weight: 500; }
.option__hint { font-size: var(--text-xs); color: var(--color-text-mute); }

.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-primary);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--text-sm);
}
.chip--button { border-color: var(--color-border); background: var(--color-surface); color: inherit; cursor: pointer; font: inherit; font-size: var(--text-sm); min-height: 40px; padding: 6px 14px; }
.chip--button.chip--on { border-color: var(--color-primary); background: var(--color-primary-soft); color: var(--color-primary); }
.chip__x { background: none; border: 0; color: inherit; cursor: pointer; font: inherit; padding: 0 0 0 2px; }
.native-date {
  font: inherit;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  max-width: 220px;
}
.ask__buttons { display: flex; }
.ask__buttons > * { flex: 1; }
.ask__hint { margin: 0; font-size: var(--text-xs); color: var(--color-text-mute); text-align: center; }
</style>
